import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

/**
 * The prototype's dropdown: a trigger button plus a panel that closes on an
 * outside click or Escape. Panel alignment matches `.dd-panel` / `.dd-panel.left`.
 */
export function Dropdown({
  trigger,
  children,
  align = 'right',
  panelStyle,
  buttonStyle,
  buttonClassName = 'dd-btn',
}: {
  trigger: ReactNode
  children: (close: () => void) => ReactNode
  align?: 'left' | 'right'
  panelStyle?: React.CSSProperties
  buttonStyle?: React.CSSProperties
  buttonClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className={open ? 'dd open' : 'dd'} ref={root}>
      <button
        className={buttonClassName}
        type="button"
        style={buttonStyle}
        onClick={() => setOpen((v) => !v)}
      >
        {trigger}
      </button>
      <div className={align === 'left' ? 'dd-panel left' : 'dd-panel'} hidden={!open} style={panelStyle}>
        {children(() => setOpen(false))}
      </div>
    </div>
  )
}
